import React, { useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import { AxiosError } from "axios";
import { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import colors from "common/styles/colors";
import { Typography } from "common/components/Typography";
import { ContentCard } from "../../components/ContentCard";
import { useMatchRateData } from "../../hooks/matchRate.hooks";
import {
  MatchRateFilters,
  MatchRateGroupBy,
} from "../../types/matchRate.types";
import {
  MatchRateFilterForm,
  MatchRateFiltersBar,
} from "./MatchRateFiltersBar";
import { MatchRateBreakdownChart } from "./MatchRateBreakdownChart";
import { MatchRateSummaryCards } from "./MatchRateSummaryCards";

const emptyFilters: MatchRateFilterForm = {
  soldDateFrom: null,
  soldDateTo: null,
  designationTypeIds: [],
  areaFrom: "",
  areaTo: "",
  constructionYearFrom: "",
  constructionYearTo: "",
};

const toIsoStart = (value: Dayjs) =>
  value.startOf("day").toDate().toISOString();

const toIsoEnd = (value: Dayjs) => {
  const date = value.format("YYYY-MM-DD");
  return `${date}T23:59:59.999Z`;
};

const parseOptionalNumber = (value: string) => {
  if (value === "") {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const buildMatchRateFilters = (
  form: MatchRateFilterForm
): MatchRateFilters => {
  const filters: MatchRateFilters = {};

  if (form.soldDateFrom) {
    filters.soldDateFrom = toIsoStart(form.soldDateFrom);
  }
  if (form.soldDateTo) {
    filters.soldDateTo = toIsoEnd(form.soldDateTo);
  }
  if (form.polygon && form.polygon.length >= 3) {
    filters.polygon = form.polygon;
  }
  if (form.designationTypeIds.length > 0) {
    filters.designationTypeIds = form.designationTypeIds;
  }

  const areaFrom = parseOptionalNumber(form.areaFrom);
  const areaTo = parseOptionalNumber(form.areaTo);
  const constructionYearFrom = parseOptionalNumber(form.constructionYearFrom);
  const constructionYearTo = parseOptionalNumber(form.constructionYearTo);

  if (areaFrom != null) {
    filters.areaFrom = areaFrom;
  }
  if (areaTo != null) {
    filters.areaTo = areaTo;
  }
  if (constructionYearFrom != null) {
    filters.constructionYearFrom = Math.round(constructionYearFrom);
  }
  if (constructionYearTo != null) {
    filters.constructionYearTo = Math.round(constructionYearTo);
  }

  return filters;
};

const getErrorMessage = (error: unknown) => {
  const data = (error as AxiosError<{ errors?: string[]; message?: string }>)
    ?.response?.data;
  if (typeof data === "string") {
    return data;
  }
  if (data && Array.isArray(data.errors) && data.errors.length > 0) {
    return data.errors.join(", ");
  }
  if (data && typeof data.message === "string") {
    return data.message;
  }
  return undefined;
};

export const MatchRateRoute: React.FC = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState<MatchRateFilterForm>(emptyFilters);
  const [groupBy, setGroupBy] = useState<MatchRateGroupBy>("month");
  const filters = useMemo(() => buildMatchRateFilters(form), [form]);
  const { summary, breakdown, isInitialLoading, error, isForbidden } =
    useMatchRateData(filters, groupBy);

  return (
    <ContentCard title={t("matchRate.title")}>
      <MatchRateFiltersBar filters={form} onChange={setForm} />
      {isInitialLoading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            margin: "24px 0",
          }}
        >
          <CircularProgress />
        </div>
      )}
      {isForbidden && (
        <Typography textStyle={{ color: colors.red, marginBottom: 16 }}>
          {t("matchRate.noAccess")}
        </Typography>
      )}
      {!!error && !isForbidden && (
        <Typography textStyle={{ color: colors.red, marginBottom: 16 }}>
          {getErrorMessage(error) ?? t("matchRate.requestError")}
        </Typography>
      )}
      {!!summary && <MatchRateSummaryCards summary={summary} />}
      <MatchRateBreakdownChart
        breakdown={breakdown}
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
      />
    </ContentCard>
  );
};
